import type { DataGridPin } from "../columns/types";

interface StickyPinsInput {
    containerWidth: number;
    pinnedStartWidth: number;
    pinnedEndWidth: number;
    /** Widths of the columns that scroll between the pinned ones. */
    centerWidths: readonly number[];
}

export interface StickyPins {
    isStartSticky: boolean;
    isEndSticky: boolean;
}

const getRoom = (centerWidths: readonly number[]) =>
    centerWidths.length > 0 ? Math.min(...centerWidths) : 0;

/**
 * Which pinned sides stick. A side that would leave less room than the
 * narrowest scrolling column scrolls with the row instead, end side first,
 * so scrolled text never shows through a sliver between pinned columns and
 * end pins never cover start pins. An unmeasured container keeps both.
 */
export const getStickyPins = ({
    containerWidth,
    pinnedStartWidth,
    pinnedEndWidth,
    centerWidths,
}: StickyPinsInput): StickyPins => {
    if (containerWidth <= 0) {
        return { isStartSticky: true, isEndSticky: true };
    }
    const room = getRoom(centerWidths);
    const isStartSticky = pinnedStartWidth + room <= containerWidth;
    const stuckStartWidth = isStartSticky ? pinnedStartWidth : 0;
    return {
        isStartSticky,
        isEndSticky: stuckStartWidth + pinnedEndWidth + room <= containerWidth,
    };
};

/**
 * How many px a pinned side can grow (a resize drag) before `getStickyPins`
 * would let a side scroll, which would pull the dragged edge away from the
 * pointer. A side that already scrolls grows freely.
 */
export const getPinGrowthLimit = ({
    side,
    ...input
}: StickyPinsInput & { side: DataGridPin }): number => {
    const { isStartSticky, isEndSticky } = getStickyPins(input);
    const isSticky = side === "start" ? isStartSticky : isEndSticky;
    if (input.containerWidth <= 0 || !isSticky) {
        return Number.POSITIVE_INFINITY;
    }
    const stuckWidth =
        (isStartSticky ? input.pinnedStartWidth : 0) +
        (isEndSticky ? input.pinnedEndWidth : 0);
    return Math.max(
        0,
        input.containerWidth - getRoom(input.centerWidths) - stuckWidth,
    );
};
